<template>
  <div
    :id="id"
    v-apply-post-links-styles
    class="element group social-ddg"
    :class="className"
    :style="socialElementStyles"
  >
    <transition
      :enter-active-class="'animated ' + animationIn"
      :leave-active-class="'animated ' + animationOut"
      :duration="{ leave: leaveDuration }"
      mode="out-in"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
    >
      <div
        v-show="toggle"
        class="group-wrap"
        :style="animationStyles"
      >
        <div
          v-if="currentPost"
          :key="currentPost.post_id"
          :style="socialDataContentOffset"
        >
          <element-renderer
            v-for="cId in childElementsReversed"
            v-show="!getElementById(cId)?.layerHiddenInGraphics"
            :id="cId"
            :key="cId"
            :type="getElementById(cId).type"
            :toggle="toggle"
            :parent="id"
            :namespace="id"
            :current-post="currentPost"
            :mute-preview="mutePreview"
            v-bind="$attrs"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import socialDataModule from '../../../store/modules/socialData';
import ElementsMixins from '../../../mixins/elements-mixins';
import {
  computeElementBounds,
  getSocialDataContentOffset,
} from '../../../store/modules/graphicsTemplate/utilities';

export default {
  name: 'SocialDataElement',
  inheritAttrs: false,
  components: {
    ElementRenderer: defineAsyncComponent(() => import('../ElementRenderer')),
  },
  mixins: [ElementsMixins],
  props: {
    toggle: {
      type: Boolean,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters('graphicsTemplate', [
      'getElementById',
      'getElementGroupMap',
      'getCanvas',
    ]),
    ...mapState({
      tagboards: (state) => state.user.tagboards,
      // namespaced - arrow functions don't work here because of the scope of this
      tagboardId(state) {
        return state[this.id].tagboardId;
      },
      mutePreview(state) {
        return state[this.id].mutePreview;
      },
      map(state) {
        return state[this.id].map;
      },
      // same with mapGetters
      getPostByIndex(state, getters) {
        return getters[`${[this.id]}/getPostByIndex`];
      },
      displayedPostIndex(state) {
        return state[this.id].displayedPostIndex;
      },
    }),
    currentPost() {
      return this.getPostByIndex(this.displayedPostIndex);
    },
    childElements() {
      return this.getElementGroupMap[this.id] || [];
    },

    childElementsReversed() {
      return [...this.childElements].reverse();
    },
    className() {
      const {
        animationInStyle, animationOutStyle,
      } = this.element;

      return [
        `social-dgg--${this.currentPost?.network}`,
        animationInStyle,
        animationOutStyle,
        (!this.element.postSettings.showOtherLinks ? 'hide-link' : ''),
        (this.element.postSettings.removeSpaces ? 'remove-spaces' : ''),
      ];
    },
    /**
     * Bounds (top, left, width, height) covering all of this element's children,
     * computed the same way regular group bounds are. Children are stored with
     * canvas-relative positions, so the wrapper is anchored at the bounds'
     * top/left and the content is counter-translated back into canvas space via
     * `socialDataContentOffset`. Animations that translate by a percentage of
     * the element (e.g. slide in/out) then travel the content's own bounds
     * instead of the whole canvas.
     */
    socialDataBounds() {
      return computeElementBounds(this.childElements, this.getElementById);
    },

    socialDataContentOffset() {
      return getSocialDataContentOffset(this.socialDataBounds);
    },

    socialElementStyles() {
      const { width: canvasWidth, height: canvasHeight } = this.getCanvas;
      const bounds = this.socialDataBounds || {};

      return {
        width: `${bounds.width ?? canvasWidth}px`,
        height: `${bounds.height ?? canvasHeight}px`,
        top: `${bounds.top ?? 0}px`,
        left: `${bounds.left ?? 0}px`,
        opacity: 1,
        transform: 'translate3d(0px, 0px, 0px)',
        position: 'absolute',
      };
    },
  },
  async created() {
    // setup the dynamic store module for this social data instance
    const store = this.$store;
    const namespace = this.id;
    const stateModuleExists = store?.hasModule(namespace);
    // register a new module only if doesn't exist
    if (!stateModuleExists) {
      store.registerModule(namespace, socialDataModule);
      // otherwise it will re-use the already existing module
      this.$store.dispatch(`${namespace}/setNamespace`, namespace);
    }

    // populate the graphic with the default tagboard, which is always injected at the 0 index
    // of the tagboards array in the graphics store
    this.updateTagboardId(this.tagboards[0]._id);
    // go get all the posts from the default tagboard
    await this.fetchFeaturedPosts();
  },
  methods: {
    // map Actions for dynamic store module namespaced for element id
    // must map this way because of dynamic namespace that comes from prop value
    ...mapActions({
      updatePosts(dispatch, payload) {
        return dispatch(`${this.id}/updatePosts`, payload);
      },
      updateTagboardId(dispatch, payload) {
        return dispatch(`${this.id}/updateTagboardId`, payload);
      },
      updateMutePreview(dispatch, payload) {
        return dispatch(`${this.id}/updateMutePreview`, payload);
      },
      updateLayoutOption(dispatch, payload) {
        return dispatch(`${this.id}/updateLayoutOption`, payload);
      },
      fetchFeaturedPosts(dispatch, payload) {
        return dispatch(`${this.id}/fetchFeaturedPosts`, payload);
      },
    }),
  },
};
</script>
